import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowRight, CheckCircle2, Receipt, TrendingUp } from 'lucide-react';
import {
  ContextHero,
  DataCard,
  DateBadge,
  EmptyStateModern,
  KpiCard,
  MoneyAmount,
  PageContainer,
  SectionTitle,
  SkeletonShimmer,
  StatusBadge,
  type UniversalStatus,
} from '@/components/design-system';
import { useFormationB2BFactures, useFormationB2BKpis } from '@/hooks/queries/useFormationB2B';

const STATUT_TONE: Record<string, UniversalStatus> = {
  ouverte: 'en_attente',
  partiellement_payee: 'en_cours',
  payee: 'paye',
  en_retard: 'en_retard',
  mise_en_demeure: 'critique',
  contentieux: 'critique',
  annulee: 'annule',
};

export function FacturesTab(): JSX.Element {
  const { data: k, isLoading: kpisLoading } = useFormationB2BKpis();
  const { data: factures = [], isLoading } = useFormationB2BFactures();

  const tauxRecouvrement = (() => {
    const total = (k?.factures_encaisse_30j ?? 0) + (k?.factures_en_attente ?? 0);
    if (total === 0) return 0;
    return Math.round(((k?.factures_encaisse_30j ?? 0) / total) * 100);
  })();

  return (
    <PageContainer>
      <div className="space-y-6">
        <ContextHero
          variant="echeances"
          icon={<Receipt className="h-5 w-5" />}
          title="Factures Clients B2B"
          subtitle="Suivez l'émission, le dépôt, l'encaissement et les retards par client."
        />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            variant="echeances"
            label="Facturé en attente"
            value={<MoneyAmount amount={k?.factures_en_attente ?? 0} variant="inline" />}
            icon={<Receipt className="h-4 w-4" />}
            loading={kpisLoading}
          />
          <KpiCard
            variant="echeances"
            label="Encaissé 30j"
            value={<MoneyAmount amount={k?.factures_encaisse_30j ?? 0} variant="inline" />}
            icon={<CheckCircle2 className="h-4 w-4" />}
            loading={kpisLoading}
          />
          <KpiCard
            variant="finances"
            label="En retard"
            value={k?.factures_en_retard ?? 0}
            icon={<AlertCircle className="h-4 w-4" />}
            loading={kpisLoading}
          />
          <KpiCard
            variant="echeances"
            label="Taux recouvrement"
            value={`${tauxRecouvrement}%`}
            icon={<TrendingUp className="h-4 w-4" />}
            loading={kpisLoading}
          />
        </div>

        <SectionTitle
          trailing={
            <Link
              to="/echeances/a-encaisser"
              className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
            >
              Module Échéances <ArrowRight className="h-3 w-3" />
            </Link>
          }
        >
          {factures.length} facture{factures.length > 1 ? 's' : ''}
        </SectionTitle>

        {isLoading ? (
          <SkeletonShimmer variant="table" count={5} />
        ) : factures.length === 0 ? (
          <EmptyStateModern
            variant="echeances"
            icon={<Receipt className="h-7 w-7" />}
            title="Aucune facture client"
            description="Les factures apparaissent automatiquement quand une session est convertie en échéance à encaisser."
            ctaLabel="Créer une échéance"
            onCtaClick={() => (window.location.href = '/echeances/a-encaisser')}
          />
        ) : (
          <DataCard>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-widest text-slate-400">
                  <tr>
                    <th className="py-2">N° Facture</th>
                    <th className="py-2">Client</th>
                    <th className="py-2">Date émission</th>
                    <th className="py-2">Échéance</th>
                    <th className="py-2 text-right">HT</th>
                    <th className="py-2 text-right">TTC</th>
                    <th className="py-2 text-right">Dû</th>
                    <th className="py-2">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {factures.map((f, i) => (
                    <motion.tr
                      key={f.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(i, 8) * 0.02 }}
                      className="border-t border-slate-100"
                    >
                      <td className="py-2 font-mono">{f.facture_ref ?? f.reference}</td>
                      <td className="py-2 font-semibold text-slate-800">{f.client_nom ?? '—'}</td>
                      <td className="py-2">
                        <DateBadge date={f.date_emission} />
                      </td>
                      <td className="py-2">
                        <DateBadge date={f.date_echeance} />
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={f.montant_ht} variant="inline" />
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={f.montant_ttc} variant="inline" />
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={f.montant_du} variant="inline" />
                      </td>
                      <td className="py-2">
                        <StatusBadge
                          status={STATUT_TONE[f.statut] ?? 'en_attente'}
                          label={f.statut.replace(/_/g, ' ')}
                        />
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataCard>
        )}
      </div>
    </PageContainer>
  );
}
